/**
 * Dense per-trip timeline built once from a shape slice + schedule (one-shot at poll).
 *
 * Columnar layout (structure-of-arrays): `length` may be less than the shape view point count
 * after sparse gaps are dropped in {@link buildTimeline}. Stored per `trip_id` in the cache.
 *
 * Hot path ({@link InterpolationEngine}): read `timeline.timestampS[i]`, `timeline.lat[i]`,
 * `timeline.lon[i]`, `timeline.bearing[i]` directly — no getters in the per-frame loop.
 */
export class CompactTimeline {

    /** Dense point count after build (not necessarily equal to shape view length). */
    readonly length: number;

    /** Scheduled time in seconds since midnight (strictly increasing after build). */
    readonly timestampS: Float64Array;

    /** Latitude in degrees (WGS-84), parallel to `timestampS`. */
    readonly lat: Float64Array;

    /** Longitude in degrees (WGS-84), parallel to `timestampS`. */
    readonly lon: Float64Array;

    /** Heading in degrees (`bearingX10 / 10` at timeline build time). */
    readonly bearing: Float64Array;

    /** GTFS stop sequence when {@link isStop} is 1, else 0. */
    readonly stopSequence: Uint16Array;

    /** 1 when the point is a stop vertex, else 0. */
    readonly isStop: Uint8Array;

    /**
     * @param length Dense point count.
     * @param timestampS Scheduled timestamps (seconds since midnight).
     * @param lat Latitudes in degrees.
     * @param lon Longitudes in degrees.
     * @param bearing Headings in degrees.
     * @param stopSequence GTFS stop sequence when `isStop` is set.
     * @param isStop Stop flags (0 or 1).
     */
    constructor(
        length: number,
        timestampS: Float64Array,
        lat: Float64Array,
        lon: Float64Array,
        bearing: Float64Array,
        stopSequence: Uint16Array,
        isStop: Uint8Array,
    ) {
        this.length = length;
        this.timestampS = timestampS;
        this.lat = lat;
        this.lon = lon;
        this.bearing = bearing;
        this.stopSequence = stopSequence;
        this.isStop = isStop;
    }

}
